/**
 * User GraphQL Queries & Mutations
 * 프로필 수정, 회사 관련 쿼리/뮤테이션
 *
 * 웹 패키지와 동일한 쿼리 구조 유지 (DRY 원칙)
 */
import { gql } from "@apollo/client";

// ============================================================================
// 프로필 관련 Mutations
// ============================================================================

/**
 * 프로필 수정 Mutation
 * 사용자 이름 변경
 */
export const UPDATE_MY_PROFILE = gql`
  mutation UpdateMyProfile($input: UpdateMyProfileInput!) {
    updateMyProfile(input: $input) {
      success
      message
    }
  }
`;

// ============================================================================
// 온보딩 관련 Queries & Mutations
// ============================================================================

/**
 * 온보딩 필요 여부 확인 Query
 * companyId가 null인 신규 가입자 감지
 */
export const NEEDS_ONBOARDING = gql`
  query NeedsOnboarding {
    needsOnboarding
  }
`;

/**
 * 테스트 회사 생성 Mutation
 * 데모 모드로 즉시 시작할 때 사용
 */
export const CREATE_TEST_COMPANY = gql`
  mutation CreateTestCompany {
    createTestCompany {
      _id
      name
      token
    }
  }
`;

// ============================================================================
// 회사 관련 Queries & Mutations
// ============================================================================

/**
 * 현재 회사 전환 Mutation
 * Issue #466: 토큰을 반환받아 Preferences에 저장
 */
export const SET_CURRENT_COMPANY = gql`
  mutation SetCurrentCompany($companyId: ID!) {
    setCurrentCompany(companyId: $companyId) {
      success
      token
    }
  }
`;

/**
 * 회사 검색 Query
 * 사업자번호 또는 회사명으로 검색
 */
export const SEARCH_COMPANIES = gql`
  query SearchCompanies($input: SearchCompaniesInput!) {
    searchCompanies(input: $input) {
      _id
      name
      businessNumber
      ceoName
      memberCount
      canRequest
    }
  }
`;

/**
 * 회사 가입 신청 Mutation
 */
export const REQUEST_JOIN_COMPANY = gql`
  mutation RequestJoinCompany($input: RequestJoinCompanyInput!) {
    requestJoinCompany(input: $input) {
      _id
      companyId
      companyName
      status
    }
  }
`;

/**
 * 내 회사 목록 조회 Query
 * useAuth 및 회사 전환 기능에서 사용
 */
export const MY_COMPANIES = gql`
  query MyCompanies {
    myCompanies {
      _id
      companyId
      companyName
      role
      status
      joinedAt
      isTest
    }
  }
`;

// ============================================================================
// 데모 상태 조회 Query
// ============================================================================

/**
 * 데모(테스트) 회사의 사용량 상태 조회
 * 프로젝트/설비 한도 도달 여부 및 현재 사용량 반환
 */
export const DEMO_STATUS = gql`
  query DemoStatus {
    demoStatus {
      isProjectLimitReached
      isMachineLimitReached
      projectCount
      projectLimit
      machineCount
      machineLimit
    }
  }
`;

// ============================================================================
// TypeScript 타입 정의
// ============================================================================

/** 온보딩 필요 여부 응답 데이터 */
export interface NeedsOnboardingData {
  needsOnboarding: boolean;
}

/** 테스트 회사 생성 결과 */
export interface TestCompany {
  _id: string;
  name: string;
  token?: string;
}

/** 테스트 회사 생성 응답 데이터 */
export interface CreateTestCompanyData {
  createTestCompany: TestCompany;
}

/** 프로필 수정 응답 */
export interface MutationResponse {
  success: boolean;
  message: string;
}

/** 프로필 수정 응답 데이터 */
export interface UpdateMyProfileData {
  updateMyProfile: MutationResponse;
}

/** 회사 전환 결과 */
export interface SetCurrentCompanyResult {
  success: boolean;
  token?: string;
}

/** 회사 전환 응답 데이터 */
export interface SetCurrentCompanyData {
  setCurrentCompany: SetCurrentCompanyResult;
}

/** 회사 검색 결과 항목 */
export interface CompanySearchResult {
  _id: string;
  name: string;
  businessNumber?: string;
  ceoName?: string;
  memberCount: number;
  canRequest: boolean;
}

/** 회사 검색 응답 데이터 */
export interface SearchCompaniesData {
  searchCompanies: CompanySearchResult[];
}

/** 가입 신청 결과 */
export interface JoinRequestResult {
  _id: string;
  companyId: string;
  companyName: string;
  status: "pending" | "approved" | "rejected";
}

/** 가입 신청 응답 데이터 */
export interface RequestJoinCompanyData {
  requestJoinCompany: JoinRequestResult;
}

/** 내 회사 정보 */
export interface MyCompany {
  _id: string;
  companyId: string;
  companyName: string;
  role: "manager" | "user";
  status: "pending" | "approved" | "rejected";
  joinedAt: string;
  isTest: boolean;
}

/** 내 회사 목록 응답 데이터 */
export interface MyCompaniesData {
  myCompanies: MyCompany[];
}

// ============================================================================
// 데모 상태 타입 정의
// ============================================================================

/** 데모 상태 응답 타입 (서버 DemoStatusType과 동기화) */
export interface DemoStatus {
  isProjectLimitReached: boolean;
  isMachineLimitReached: boolean;
  projectCount: number;
  projectLimit: number;
  machineCount: number;
  machineLimit: number;
}

/** 데모 상태 쿼리 응답 데이터 (서버 nullable: true) */
export interface DemoStatusData {
  demoStatus: DemoStatus | null;
}

// ============================================================================
// Input 타입 정의
// ============================================================================

/** 프로필 수정 입력 */
export interface UpdateMyProfileInput {
  name: string;
}

/** 회사 검색 입력 */
export interface SearchCompaniesInput {
  keyword: string;
  limit?: number;
}

/** 가입 신청 입력 */
export interface RequestJoinCompanyInput {
  companyId: string;
}
